import { Model } from "@vuex-orm/core"
import Course from "@/models/Course"
import Student from "@/models/Student"
import { notify } from "@/services/notification"
import Presence from "@/models/Presence"
import Checkin from "@/models/Checkin"
import { parseISO, format } from "date-fns"

/**
 * Normalize a date value to "YYYY-MM-DD" format.
 * Handles ISO datetime strings (e.g. "2024-01-15T00:00:00.000000Z"),
 * plain date strings ("2024-01-15"), and null/undefined.
 */
function toDateOnly(value) {
  if (!value) return null
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value
  try {
    return format(parseISO(value), "yyyy-MM-dd")
  } catch {
    return null
  }
}

export default class StudentCourse extends Model {
  static get entity() {
    return "student_courses"
  }

  static fields() {
    return {
      id: this.attr(0),
      student_id: this.attr(0),
      course_id: this.attr(0),

      // Course related
      level: this.attr(""),

      // Enrollment dates — mutators ensure "YYYY-MM-DD" format
      started_at: this.attr(null, (value) => toDateOnly(value)),
      stopped_at: this.attr(null, (value) => toDateOnly(value)),

      // descriptions
      descriptions: this.attr({}),
      absenceCount: this.attr(0),

      // optional
      assistance: this.attr(""),
      student: this.belongsTo(Student, "student_id"),
      course: this.belongsTo(Course, "course_id"),
      course_type: this.attr(""),
      last_sent_notification: this.attr(""),
      notification_status: this.attr(""),
      presences: this.hasMany(Presence, "student_course_id"),
      checkins: this.hasMany(Checkin, "student_course_id"),
    }
  }

  get hasStopped() {
    return this.stopped_at !== null
  }

  get isLanguageCourse() {
    return this.course_type === "fr" || this.course_type === "frdistance" || this.course_type === "re" || this.course_type === "cp"
  }

  get isJobCourse() {
    return this.course_type == "re"
  }

  get isItCourse() {
    return this.course_type == "it"
  }

  static async getOne(student_course_id) {
    return await this.api()
      .get(`/student_course/${student_course_id}`, { dataKey: "data" })
      .catch((error) => {
        notify("error", error)
        return error
      })
  }

  static async put(updatedSc) {
    return await this.api()
      .put(`/student_course/`, updatedSc)
      .then(() => {
        notify("success", "Reussi")
      })
      .catch((error) => {
        notify("error", error)
      })
  }

  static async getEnhancedStudentCoursesBetweenDates(date_start, date_end) {
    return await this.api()
      .get(`/student_course/enhanced_between_dates?start=${date_start}&end=${date_end}`, {
        dataKey: "data",
      })
      .catch((error) => {
        notify("error", error)
        return error
      })
  }

  static async getStudentCoursesBetweenDates(date_start, date_end) {
    return await this.api()
      .get(`/student_course/between_dates?start=${date_start}&end=${date_end}`, {
        dataKey: "data",
      })
      .catch((error) => {
        notify("error", error)
        return error
      })
  }

  static async stopEnrollment(student_course_id, stopped_at) {
    return await this.api()
      .post(`/student_course/${student_course_id}/stop`, { stopped_at })
      .then(() => {
        notify("success", "Inscription terminée")
      })
      .catch((error) => {
        notify("error", error)
      })
  }

  static async updateEnrollmentDates(student_course_id, { started_at, stopped_at }) {
    return await this.api()
      .put(`/student_course/${student_course_id}/enrollment`, { started_at, stopped_at })
      .then(() => {
        notify("success", "Dates mises à jour")
      })
      .catch((error) => {
        notify("error", error)
      })
  }

  static async unsubscribe(student_course_id) {
    return await this.api()
      .post(`/students_unsubscribe/${student_course_id}`)
      .then(() => {
        this.delete(student_course_id)
        notify("success", "Élève désinscrit")
      })
      .catch((error) => {
        notify("error", error)
      })
  }

  static async getStudentCoursesByStudentId(student_id) {
    return await this.api()
      .get(`/student_course/student/${student_id}`, { dataKey: "data" })
      .catch((error) => {
        notify("error", error)
      })
  }
}
